import _ from 'lodash';
import React from 'react';
import postal from 'postal';
import PropTypes from 'prop-types';
import toastr from 'toastr';

import signUpAsync from '../../services/auth/sign-up-async';
import ErrorBoundary from '../../shared/error-boundary';
import mutate from '../../shared/mutate';
import breakpoints from '../../shared/breakpoints';
import Validator from '../../shared/validator';
import parseFailXhr from '../../shared/parse-fail-xhr';
import Loading from '../../shared/loading';
import { EMAIL_REGEX, MINIMUM_PASSWORD_LENGTH } from '../../shared/consts';

function helpBlock(message) {
  return <span className="help-block">{message}</span>;
}

export default class CampaignSignUpForm extends React.Component {
  static propTypes = {
    showPreHeaderCopy: PropTypes.bool,
    redirectTo: PropTypes.string,
    title: PropTypes.string,
    email: PropTypes.string,
  };

  static defaultProps = {
    showPreHeaderCopy: false,
    redirectTo: null,
    title: 'Register free today',
    email: '',
  };

  constructor(props, context) {
    super(props, context);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleFirstNameChange = this.handleFirstNameChange.bind(this);
    this.handleLastNameChange = this.handleLastNameChange.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    // eslint-disable-next-line max-len
    this.handleTermsAndConditionsAcceptedChange = this.handleTermsAndConditionsAcceptedChange.bind(this);
    this.openSignInModal = this.openSignInModal.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handleVerifyPasswordChange = this.handleVerifyPasswordChange.bind(this);
    this.present = this.present.bind(this);
    this.dismiss = this.dismiss.bind(this);

    this.state = {
      firstName: '',
      lastName: '',
      email: props.email,
      emailIsUnique: true,
      // eslint-disable-next-line react/no-unused-state
      termsAndConditionsAccepted: false,
      password: '',
      // eslint-disable-next-line react/no-unused-state
      verifyPassword: '',
      validator: new Validator({
        lastNameRequired: state => !this.$lastNameInput.is(':visible') || !!state.lastName,
        emailRequired: state => !!state.email,
        emailRegex: state => !!state.email || EMAIL_REGEX.test(state.email),
        emailIsUnique: state => state.emailIsUnique,
        termsAndConditionsMustBeAccepted: state => state.termsAndConditionsAccepted,
        passwordRequired: state => !this.$passwordInput.is(':visible') || !!state.password,
        passwordCorrectLength: state => !this.$passwordInput.is(':visible') || !state.password || state.password.length >= MINIMUM_PASSWORD_LENGTH,
        passwordContainsDigit: state => !this.$passwordInput.is(':visible') || !state.password || /[0-9]/.test(state.password),
        verifyPasswordRequired: state => !this.$passwordInput.is(':visible') || !!state.verifyPassword,
        passwordsMustMatch: state => !this.$passwordInput.is(':visible') || !state.password || !state.verifyPassword || state.password === state.verifyPassword,
      }),
    };
    this.firstNameInput = React.createRef();
    this.emailInput = React.createRef();
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'commands.campaigns',
      topic: 'present-sign-up-modal',
      callback: () => this.present(),
    });

    let onMobileOrTablet = breakpoints.upTo_medium();
    $(window).on('resize', _.throttle(() => {
      const currentlyOnMobileOrTablet = breakpoints.upTo_medium();

      if (currentlyOnMobileOrTablet !== onMobileOrTablet) {
        onMobileOrTablet = currentlyOnMobileOrTablet;

        this.forceUpdate();
        if (onMobileOrTablet) {
          this.$campaignSignUpForm.hide();
        } else {
          this.$campaignSignUpForm.show();
        }
      }
    }, 1000 / 60));
  }

  handleSubmit(event) {
    if (event) event.preventDefault();

    this.setState(
      state => state.validator.validateForm(state),
      () => {
        if (!this.state.validator.formValid) return;

        this.loading.start();

        signUpAsync({
          email: this.state.email,
          firstName: this.state.firstName,
          lastName: this.state.lastName,
          password: this.state.password,
        }).done(data => {
          const redirectPart = this.props.redirectTo ? `&redirect_to=${encodeURIComponent(this.props.redirectTo)}` : '';

          window.location = `/users/confirmation?confirmation_token=${data.confirmation_token}${redirectPart}`;
        }).fail(xhr => {
          this.loading.stop();

          const errors = parseFailXhr(xhr);

          if (errors.email && errors.email.hasAlreadyBeenTaken) {
            this.setState(oldState => mutate(oldState, state => {
              state.emailIsUnique = false;
              state.validator.validateEmailIsUnique(state);
            }));
            errors.email.hasAlreadyBeenTaken.handled = true;
          }

          if (errors.anyUnhandledErrors()) {
            toastr.error(errors.getErrorSummary(), 'Unhandled errors');
          }
        });
      },
    );
  }

  handleFirstNameChange(event) {
    this.setState({ firstName: event.target.value });
  }

  handleLastNameChange(event) {
    const lastName = event.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.lastName = lastName;
      state.validator.validateLastNameRequired(state);
    }));
  }

  handleEmailChange(event) {
    const email = event.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.email = email;
      state.emailIsUnique = true;
      state.validator.validateEmailRequired(state);
      state.validator.validateEmailRegex(state);
      state.validator.validateEmailIsUnique(state);
    }));
  }

  handleTermsAndConditionsAcceptedChange(event) {
    const termsAndConditionsAccepted = event.target.checked;

    this.setState(oldState => mutate(oldState, state => {
      state.termsAndConditionsAccepted = termsAndConditionsAccepted;
      state.validator.validateTermsAndConditionsMustBeAccepted(state);
    }));
  }

  handlePasswordChange(event) {
    const password = event.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.password = password;
      state.validator.validatePasswordRequired(state);
    }));
  }

  handleVerifyPasswordChange(event) {
    const verifyPassword = event.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.verifyPassword = verifyPassword;
      state.validator.validateVerifyPasswordRequired(state);
      state.validator.validatePasswordsMustMatch(state);
    }));
  }

  prefillEmail(email) {
    this.emailInput.current.value = email;
  }

  present() {
    if (breakpoints.upTo_medium()) {
      this.$campaignSignUpForm.fadeIn();
      $('body').addClass('has-fullscreen-modal');
    } else {
      $('html,body').animate({ scrollTop: 0 }, 400);
    }
  }

  dismiss() {
    this.$campaignSignUpForm.fadeOut();
    $('body').removeClass('has-fullscreen-modal');
  }

  focusInput() {
    const firstNameInputRef = this.firstNameInput.current;
    firstNameInputRef.focus();
  }

  // eslint-disable-next-line class-methods-use-this
  openSignInModal(event) {
    if (event) event.preventDefault();
    postal.publish({
      channel: 'commands.sign-in',
      topic: 'present',
    });
  }

  renderHeader() {
    const onMobileOrTablet = breakpoints.upTo_medium();
    return (
      <header>
        {!onMobileOrTablet && this.props.showPreHeaderCopy
          && (
          <p>
            The content you&apos;re trying to access is exclusive to Livewire members.&nbsp;
            It&apos;s free to become a member, so...
          </p>
          )}
        {!onMobileOrTablet && (<h3>{this.props.title}</h3>)}
        {onMobileOrTablet && (
          <React.Fragment>
            <h4>{this.props.title}</h4>
            <button type="button" className="close-modal" onClick={this.dismiss}>
              <span className="pe-7s-close pe-icon-x3" />
              <span className="sr-only">Close</span>
            </button>
          </React.Fragment>
        )}
      </header>
    );
  }

  renderFirstName() {
    return (
      <li>
        <input
          ref={this.firstNameInput}
          type="text"
          className="form-control"
          placeholder="First name"
          onChange={this.handleFirstNameChange}
          id="firstName"
        />
      </li>
    );
  }

  renderLastName() {
    const isValid = this.state.validator.lastNameRequiredValid;
    return (
      <li className={!isValid ? 'has-error' : ''}>
        <input
          type="text"
          className="form-control"
          placeholder="Last name"
          ref={el => (this.$lastNameInput = $(el))}
          onChange={this.handleLastNameChange}
          id="lastName"
        />
        {isValid || helpBlock('Last name is required')}
      </li>
    );
  }

  renderEmail() {
    const v = this.state.validator;
    return (
      <React.Fragment>
        <li className={!v.emailRequiredValid || !v.emailRegexValid || !v.emailIsUniqueValid ? 'has-error' : ''}>
          <input
            ref={this.emailInput}
            type="email"
            className="form-control"
            placeholder="Email address"
            onChange={this.handleEmailChange}
            id="emailAddress"
          />
          {v.emailRequiredValid || helpBlock('Email address is required')}
          {v.emailRegexValid || !v.emailRequiredValid || helpBlock('Email address is invalid')}
          {v.emailIsUniqueValid || helpBlock('Email address is already in use')}
        </li>
        {v.emailIsUniqueValid || (
          <li>
            This email address has already been used to create an account.&nbsp;
            Did you want to&nbsp;
            <a href="#" onClick={this.openSignInModal}>sign in</a>
            &nbsp;or&nbsp;
            <a href="/users/password/new">reset your password</a>
            &nbsp;instead?
          </li>
        )}
      </React.Fragment>
    );
  }

  renderPassword() {
    const v = this.state.validator;
    const passwordValid = v.passwordRequiredValid && v.passwordCorrectLengthValid;
    const verifyPasswordValid = v.verifyPasswordRequiredValid && v.passwordsMustMatchValid;
    return (
      <React.Fragment>
        <li className={!passwordValid ? 'has-error' : ''}>
          <input
            type="password"
            className="form-control"
            placeholder="Password"
            ref={el => (this.$passwordInput = $(el))}
            onChange={this.handlePasswordChange}
            id="password"
          />
          {v.passwordRequiredValid || helpBlock('Password is required')}
          {v.passwordCorrectLengthValid
            || helpBlock(`Password must be at least ${MINIMUM_PASSWORD_LENGTH} characters`)}
          {v.passwordContainsDigitValid || helpBlock('Password must include at least one digit')}
        </li>
        <li className={!verifyPasswordValid ? 'has-error' : ''}>
          <input
            type="password"
            className="form-control"
            placeholder="Verify password"
            onChange={this.handleVerifyPasswordChange}
            id="verifyPassword"
          />
          {v.verifyPasswordRequiredValid || !v.passwordRequiredValid
            || helpBlock('Verify password is required')}
          {v.passwordsMustMatchValid || helpBlock('Passwords must match')}
        </li>
      </React.Fragment>
    );
  }

  renderTC() {
    const isValid = this.state.validator.termsAndConditionsMustBeAcceptedValid;
    return (
      <React.Fragment>
        <li>
          <label htmlFor="termsAndConditionsAccepted">
            <input
              type="checkbox"
              onChange={this.handleTermsAndConditionsAcceptedChange}
              id="termsAndConditionsAccepted"
            />
            I agree to Livewire&apos;s&nbsp;
            <a href="/company/terms_and_conditions" target="_blank">Terms and Conditions</a>
            &nbsp;and&nbsp;
            <a href="/company/privacy" target="_blank">Privacy Disclaimer</a>
          </label>
        </li>
        {isValid || (
          <li className="has-error">{helpBlock('Please accept the terms and conditions')}</li>
        )}
      </React.Fragment>
    );
  }

  renderForm() {
    return (
      <form onSubmit={this.handleSubmit}>
        <Loading ref={e => (this.loading = e)} variant="small">
          <ul className="form-list">
            {this.renderFirstName()}
            {this.renderLastName()}
            {this.renderEmail()}
            {this.renderPassword()}
            {this.renderTC()}
            <li>
              <button type="submit" className="btn btn-primary" id="joinFreeButton">Join free</button>
              <p className="already-member-prompt">
                Already a member?&nbsp;
                <a href="#" onClick={this.openSignInModal} id="signInHereLink">Sign in here</a>
                .
              </p>
            </li>
          </ul>
        </Loading>
      </form>
    );
  }

  render() {
    const onMobileOrTablet = breakpoints.upTo_medium();
    return (
      <ErrorBoundary>
        <div
          ref={e => (this.$campaignSignUpForm = $(e))}
          className={`campaign-sign-up-form ${onMobileOrTablet && 'livewire-modal'}`}
          tabIndex="-1"
          role="dialog"
        >
          <article>
            {this.renderHeader()}
            <section>{this.renderForm()}</section>
          </article>
        </div>
      </ErrorBoundary>
    );
  }
}
